import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

// Custom red marker for Ampera branding (updated from orange)
const createCustomIcon = (isSelected) => {
  const color = isSelected ? '#991b1b' : '#dc2626';
  const size = isSelected ? 40 : 32;

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
        <circle cx="12" cy="9" r="3" fill="#fff"/>
      </svg>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

// User location marker
const createUserLocationIcon = () => {
  return L.divIcon({
    className: 'user-marker',
    html: `
      <div class="relative">
        <div class="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg"></div>
        <div class="absolute inset-0 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-75"></div>
      </div>
    `,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
};

// Component to handle map centering when selected branch changes
function MapController({ selectedBranch, branches, userLocation }) {
  const map = useMap();
  const initialFitDone = useRef(false);

  useEffect(() => {
    if (selectedBranch?.latitude && selectedBranch?.longitude) {
      map.flyTo([Number(selectedBranch.latitude), Number(selectedBranch.longitude)], 15, {
        duration: 1.5,
      });
    }
  }, [selectedBranch, map]);

  useEffect(() => {
    if (!initialFitDone.current && branches.length > 0) {
      const validBranches = branches.filter(b => b.latitude && b.longitude);
      if (validBranches.length > 0) {
        const points = validBranches.map(b => [Number(b.latitude), Number(b.longitude)]);

        // Include user location in bounds if available
        if (userLocation) {
          points.push([userLocation.lat, userLocation.lng]);
        }

        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
        initialFitDone.current = true;
      }
    }
  }, [branches, map, userLocation]);

  return null;
}

// Amenity icons for popup
const AMENITY_ICONS = {
  mushola: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2L2 8v2h20V8L12 2zm0 2.5L18.5 8h-13L12 4.5zM4 12v8h3v-6h2v6h2v-6h2v6h2v-6h2v6h3v-8H4z"/>
    </svg>
  ),
  wifi: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
    </svg>
  ),
  valet: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
    </svg>
  ),
  lesehan: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M4 18v3h3v-3h10v3h3v-6H4v3zm15-8h3v3h-3v-3zM2 10h3v3H2v-3zm15 3H7V5c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v8z"/>
    </svg>
  ),
  live_music: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
    </svg>
  ),
  toilets: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.5 22v-7.5H4V9c0-1.1.9-2 2-2h3c1.1 0 2 .9 2 2v5.5H9.5V22h-4zM18 22v-6h3l-2.54-7.63C18.18 7.55 17.42 7 16.56 7h-.12c-.86 0-1.63.55-1.9 1.37L12 16h3v6h3zM7.5 6c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2zm9 0c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2z"/>
    </svg>
  ),
  parking: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M13 3H6v18h4v-6h3c3.31 0 6-2.69 6-6s-2.69-6-6-6zm.2 8H10V7h3.2c1.1 0 2 .9 2 2s-.9 2-2 2z"/>
    </svg>
  ),
  ac: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22 11h-4.17l3.24-3.24-1.41-1.42L15 11h-2V9l4.66-4.66-1.42-1.41L13 6.17V2h-2v4.17L7.76 2.93 6.34 4.34 11 9v2H9L4.34 6.34 2.93 7.76 6.17 11H2v2h4.17l-3.24 3.24 1.41 1.42L9 13h2v2l-4.66 4.66 1.42 1.41L11 17.83V22h2v-4.17l3.24 3.24 1.42-1.41L13 15v-2h2l4.66 4.66 1.41-1.42L17.83 13H22z"/>
    </svg>
  ),
  outdoor: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 6l-4.22 5.63 1.25 1.67L14 9.33 19 16h-8.46l-4.01-5.37L1 18h22L14 6zM5 16l1.52-2.03L8.04 16H5z"/>
    </svg>
  ),
};

const AmenityIcon = ({ type }) => {
  return AMENITY_ICONS[type] || null;
};

export default function BranchMap({ branches, selectedBranch, onBranchSelect, userLocation }) {
  const { t } = useTranslation();

  // Default center (Indonesia)
  const defaultCenter = [-6.2088, 106.8456];

  const center = useMemo(() => {
    // If user location is available, center on that
    if (userLocation) {
      return [userLocation.lat, userLocation.lng];
    }

    const validBranches = branches.filter(b => b.latitude && b.longitude);
    if (validBranches.length === 0) return defaultCenter;

    return [
      validBranches.reduce((sum, b) => sum + Number(b.latitude), 0) / validBranches.length,
      validBranches.reduce((sum, b) => sum + Number(b.longitude), 0) / validBranches.length,
    ];
  }, [branches, userLocation]);

  return (
    <MapContainer
      center={center}
      zoom={branches.length > 0 ? 10 : 5}
      className="h-full w-full"
      scrollWheelZoom={true}
      zoomControl={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <MapController selectedBranch={selectedBranch} branches={branches} userLocation={userLocation} />

      {/* User location marker */}
      {userLocation && (
        <>
          <Circle
            center={[userLocation.lat, userLocation.lng]}
            radius={500}
            pathOptions={{
              color: '#3b82f6',
              fillColor: '#3b82f6',
              fillOpacity: 0.1,
              weight: 1
            }}
          />
          <Marker
            position={[userLocation.lat, userLocation.lng]}
            icon={createUserLocationIcon()}
          >
            <Popup>
              <div className="text-center p-1">
                <p className="font-medium text-gray-900">{t('branches.yourLocation')}</p>
              </div>
            </Popup>
          </Marker>
        </>
      )}

      {branches.map((branch) => {
        if (!branch.latitude || !branch.longitude) return null;
        const isSelected = selectedBranch?.id === branch.id;

        return (
          <Marker
            key={branch.id}
            position={[Number(branch.latitude), Number(branch.longitude)]}
            icon={createCustomIcon(isSelected)}
            eventHandlers={{
              click: () => onBranchSelect(branch),
            }}
          >
            <Popup className="branch-popup" maxWidth={300} minWidth={250}>
              <div className="p-1">
                {branch.featured_image_url && (
                  <div className="w-full h-24 -mx-1 -mt-1 mb-2 overflow-hidden rounded-t">
                    <img
                      src={branch.featured_image_url}
                      alt={branch.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <h3 className="font-semibold text-gray-900 text-base">{branch.name}</h3>
                <p className="text-gray-600 text-sm mt-1">{branch.address}</p>
                {branch.city && (
                  <p className="text-gray-500 text-xs">{branch.city}{branch.country && `, ${branch.country}`}</p>
                )}

                {/* Distance */}
                {branch.distance_km !== undefined && (
                  <p className="text-xs text-green-600 mt-1">
                    {branch.distance_km.toFixed(1)} km {t('branches.away')}
                  </p>
                )}

                {/* Amenities */}
                {branch.amenities && branch.amenities.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-1.5">{t('branches.amenities.title')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {branch.amenities.map((amenityKey) => (
                        <span
                          key={amenityKey}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs"
                          title={t(`branches.amenities.${amenityKey}`)}
                        >
                          <AmenityIcon type={amenityKey} />
                          <span className="hidden sm:inline">{t(`branches.amenities.${amenityKey}`)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-3 pt-2 border-t border-gray-100 flex gap-2">
                  <a
                    href={branch.google_maps_url || `https://www.google.com/maps/dir/?api=1&destination=${branch.latitude},${branch.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    {t('branches.getDirections')}
                  </a>
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
}
